import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  Box,
  Button,
  Input,
  VStack,
  Heading,
  Text,
  Container,
  Card,
} from "@chakra-ui/react";
import { Field } from "@chakra-ui/react";

import { useAppDispatch, useAppSelector } from "../store/hooks";
import { resetPassword } from "../store/slices/authSlice";
import { toaster } from "../components/ui/toaster";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.auth);
  const emailFromState = location.state?.email || "";

  const [formData, setFormData] = useState({
    email: emailFromState,
    code: "",
    new_password: "",
  });

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) return "Password must be at least 8 characters";
    if (!/[a-z]/.test(password)) return "Must contain lowercase letter";
    if (!/[A-Z]/.test(password)) return "Must contain uppercase letter";
    if (!/\d/.test(password)) return "Must contain a number";
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/.test(password))
      return "Must contain special character";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.code.length !== 7) {
      toaster.create({
        title: "Code must be 7 digits",
        type: "error",
        duration: 3000,
      });
      return;
    }

    const passwordError = validatePassword(formData.new_password);
    if (passwordError) {
      toaster.create({ title: passwordError, type: "error", duration: 3000 });
      return;
    }

    try {
      const result = await dispatch(resetPassword(formData)).unwrap();
      toaster.create({
        title: "Password reset",
        description: result.message || "You can now login",
        type: "success",
        duration: 5000,
      });
      navigate("/login");
    } catch (error: any) {
      toaster.create({
        title: "Reset failed",
        description: error,
        type: "error",
        duration: 5000,
      });
    }
  };

  return (
    <Box className="min-h-screen flex items-center justify-center" bg="gray.50">
      <Container maxW="md" py={12}>
        <VStack mb={6} textAlign="center">
          <Heading size="xl">Reset Password</Heading>
          <Text color="gray.600">Enter the code and your new password</Text>
        </VStack>

        <Card.Root>
          <Card.Body p={8}>
            <form onSubmit={handleSubmit}>
              <VStack gap={4}>
                <Field.Root required width="full">
                  <Field.Label>Email</Field.Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                  />
                </Field.Root>

                <Field.Root required width="full">
                  <Field.Label>Reset Code</Field.Label>
                  <Input
                    placeholder="1234567"
                    maxLength={7}
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({ ...formData, code: e.target.value })
                    }
                    required
                  />
                </Field.Root>

                <Field.Root required width="full">
                  <Field.Label>New Password</Field.Label>
                  <Input
                    type="password"
                    placeholder="Your new password"
                    value={formData.new_password}
                    onChange={(e) =>
                      setFormData({ ...formData, new_password: e.target.value })
                    }
                    required
                  />
                </Field.Root>

                <Button
                  type="submit"
                  colorScheme="blue"
                  width="full"
                  loading={loading}
                  mt={2}>
                  Reset Password
                </Button>

                <Text textAlign="center">
                  <Link
                    to="/login"
                    className="text-blue-500 hover:text-blue-600">
                    Back to login
                  </Link>
                </Text>
              </VStack>
            </form>
          </Card.Body>
        </Card.Root>
      </Container>
    </Box>
  );
}
